"use client";

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { DashboardLayout } from "@/components/dashboard-layout";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AppConfig } from "@/config/app-config";
import {
  Users,
  Home,
  Settings,
  FileText,
  ArrowUp,
  ArrowDown,
  Database,
  Monitor,
  CheckCircle,
  Wifi,
  Terminal,
} from "lucide-react";
import Link from "next/link";
 
interface SubStat {
  label: string;
  value: string;
  icon?: React.ReactNode;
  percent?: number;
}
 
interface MainStat {
  title: string;
  value: string;
  change: string;
  changeType: "up" | "down";
  href?: string;
  description: string;
  icon: React.ReactNode;
  subStats: SubStat[];
}

export default function DashboardPage() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/dashboard/summary');
        const result = await response.json();
        if (result.success) {
          setStats(result.data);
        }
      } catch (error) {
        console.error("대시보드 데이터 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // 웹소켓 상태 등을 위해 주기적으로 업데이트 (예: 10초)
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <AuthGuard>
        <DashboardLayout title="WJPNCMA 대시보드" subtitle="데이터 로딩 중...">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DashboardLayout>
      </AuthGuard>
    );
  }

  // 메인 통계 구성
  const mainStats: MainStat[] = [
    {
      title: "WebSocket 연결 현황",
      value: stats?.wsStats?.activeConnections?.toString() || "0",
      change: stats?.wsStats?.totalConnections > 0 ? "실시간" : "연결없음",
      changeType: "up",
      href: "/gas-stations/connect-service2",
      description: "현재 실시간 통신 서버 연결 상태 (클릭시 이동)",
      icon: <Wifi className="w-5 h-5 text-orange-600" />,
      subStats: [
        { label: "관리자 (브라우저)", value: stats?.wsStats?.browserConnections?.toString() || "0", icon: <Users className="w-4 h-4" /> },
        { label: "클라이언트 (C#)", value: stats?.wsStats?.csharpConnections?.toString() || "0", icon: <Terminal className="w-4 h-4" /> },
      ],
    },
    {
      title: "총 주유소 수",
      value: stats?.gasStationCount?.toLocaleString() || "0",
      change: "실제 데이터",
      changeType: "up",
      description: "시스템 등록 전체 주유소 (테스트 제외)",
      icon: <Home className="w-5 h-5 text-blue-600" />,
      subStats: [
        { label: "장애 발생 매장", value: stats?.logStats?.uniqueStores?.toString() || "0" },
        { label: "신규 등록 (최근)", value: "0" },
      ],
    },
    {
      title: "ODT 설정 현황",
      value: stats?.odtConfigCount?.toLocaleString() || "0",
      change: "실제 데이터",
      changeType: "up",
      description: "중앙 관리 시스템 XML 요소 수",
      icon: <Settings className="w-5 h-5 text-green-600" />,
      subStats: [
        { label: "설정 완료율", value: "100%", percent: 100 },
      ],
    },
  ];

  // 게시판 카테고리별 현황
  const totalPosts = stats?.boardBrief?.reduce((acc: number, curr: any) => acc + curr.count, 0) || 0;
  const boardStats = stats?.boardBrief?.map((b: any) => ({
    category: b.category,
    count: b.count,
    percentage: totalPosts > 0 ? parseFloat(((b.count / totalPosts) * 100).toFixed(1)) : 0,
    icon: b.slug === 'ssd-history' ? '💾' : '📢',
  })) || [];

  // 시스템 상태 모니터링
  const systemStatus = [
    {
      service: "데이터베이스",
      status: "정상",
      uptime: "99.9%",
      icon: <Database className="w-4 h-4" />,
    },
    {
      service: "WebSocket 서버",
      status: stats?.wsStats ? "정상" : "점검필요",
      uptime: "99.9%",
      icon: <Wifi className="w-4 h-4" />,
    },
    {
      service: "게시판 서비스",
      status: "정상",
      uptime: "99.9%",
      icon: <FileText className="w-4 h-4" />,
    },
    {
      service: "로그 수집 서버",
      status: "정상",
      uptime: "99.7%",
      icon: <Monitor className="w-4 h-4" />,
    },
  ];

  // 빠른 액션 버튼
  const quickActions = [
    {
      title: "주유소 검색",
      href: "/gas-stations",
      icon: <Home className="w-4 h-4" />,
      color: "bg-blue-500 hover:bg-blue-600",
    },
    {
      title: "ODT 설정 검색",
      href: "/odt-config/search",
      icon: <Settings className="w-4 h-4" />,
      color: "bg-green-500 hover:bg-green-600",
    },
    {
      title: "게시글 작성",
      href: "/board/write",
      icon: <FileText className="w-4 h-4" />,
      color: "bg-purple-500 hover:bg-purple-600",
    },
    {
      title: "사용자 관리",
      href: "/system/users",
      icon: <Users className="w-4 h-4" />,
      color: "bg-orange-500 hover:bg-orange-600",
    },
  ];

  return (
    <AuthGuard>
      <DashboardLayout
        title="WJPNCMA 대시보드"
        subtitle={`${new Date().toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} | 실시간 시스템 현황`}
      >
        <div className="space-y-6">
          {/* 메인 통계 카드 그리드 */}
          <div className="grid gap-6 lg:grid-cols-3">
            {mainStats.map((stat, index) => (
              <Card key={index} className={`p-6 transition-all ${stat.href ? 'cursor-pointer hover:shadow-md hover:border-primary/50' : ''}`}>
                {stat.href ? (
                  <Link href={stat.href} className="block h-full">
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {stat.icon}
                          <p className="text-xs font-semibold text-muted-foreground tracking-wide">
                            {stat.title}
                          </p>
                        </div>
                        <div className="flex items-end justify-between">
                          <h2 className="text-3xl font-bold">{stat.value}</h2>
                          <div
                            className={`flex items-center text-sm ${
                              stat.changeType === "up" ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {stat.changeType === "up" ? (
                              <ArrowUp className="w-3 h-3 mr-1" />
                            ) : (
                              <ArrowDown className="w-3 h-3 mr-1" />
                            )}
                            {stat.change}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {stat.description}
                        </p>
                      </div>

                      <div className="space-y-3">
                        {stat.subStats.map((subStat: SubStat, subIndex) => (
                          <div key={subIndex} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                {subStat.icon}
                                <span className="text-muted-foreground">
                                  {subStat.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{subStat.value}</span>
                                {subStat.percent !== undefined && (
                                  <span className="text-xs text-muted-foreground">
                                    {subStat.percent}%
                                  </span>
                                )}
                              </div>
                            </div>
                            {subStat.percent !== undefined && (
                              <Progress value={subStat.percent} className="h-1" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {stat.icon}
                        <p className="text-xs font-semibold text-muted-foreground tracking-wide">
                          {stat.title}
                        </p>
                      </div>
                      <div className="flex items-end justify-between">
                        <h2 className="text-3xl font-bold">{stat.value}</h2>
                        <div
                          className={`flex items-center text-sm ${
                            stat.changeType === "up" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {stat.changeType === "up" ? (
                            <ArrowUp className="w-3 h-3 mr-1" />
                          ) : (
                            <ArrowDown className="w-3 h-3 mr-1" />
                          )}
                          {stat.change}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {stat.description}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {stat.subStats.map((subStat: SubStat, subIndex) => (
                        <div key={subIndex} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {subStat.icon}
                              <span className="text-muted-foreground">
                                {subStat.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{subStat.value}</span>
                              {subStat.percent !== undefined && (
                                <span className="text-xs text-muted-foreground">
                                  {subStat.percent}%
                                </span>
                              )}
                            </div>
                          </div>
                          {subStat.percent !== undefined && (
                            <Progress value={subStat.percent} className="h-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* 빠른 액션 버튼 */}
          <div className="grid gap-6 lg:grid-cols-4">
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                className={`h-auto p-4 flex flex-col items-center gap-2 ${action.color} text-white border-0 shadow-sm transition-transform hover:scale-105`}
                asChild
              >
                <Link href={action.href}>
                  {action.icon}
                  <span className="text-sm font-medium">
                    {action.title}
                  </span>
                </Link>
              </Button>
            ))}
          </div>

          {/* 게시판 현황 & 최근 로그 */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* 게시판 카테고리별 현황 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">게시판 현황</CardTitle>
                <CardDescription>전체 {totalPosts}개 게시글 기준</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {boardStats.length > 0 ? boardStats.map((stat: any, index: number) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{stat.icon}</span>
                        <span className="text-sm font-medium">
                          {stat.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium">
                          {stat.count}개
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({stat.percentage}%)
                        </span>
                      </div>
                    </div>
                    <Progress value={stat.percentage} className="h-2" />
                  </div>
                )) : (
                  <div className="text-center py-10 text-muted-foreground">게시글 데이터가 없습니다.</div>
                )}
              </CardContent>
            </Card>

            {/* 최근 로그 현황 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">시스템 에러 발생 현황</CardTitle>
                <CardDescription>오늘 발생한 주요 에러 로그</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {stats?.logStats?.topErrors?.length > 0 ? stats.logStats.topErrors.map((error: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-2 h-2 bg-red-500 rounded-full shrink-0"></div>
                      <p className="font-medium text-sm truncate pr-4" title={error.message}>{error.message}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-red-600">{error.count}</p>
                      <p className="text-xs text-muted-foreground">건 발생</p>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-10 text-muted-foreground">오늘 발생한 에러 로그가 없습니다.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* 시스템 상태 모니터링 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">시스템 상태 모니터링</CardTitle>
              <CardDescription>
                현재 서비스 가동 상태 및 가동률
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {systemStatus.map((status, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      {status.icon}
                      <span className="text-sm font-medium">
                        {status.service}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <CheckCircle className={`w-3 h-3 ${status.status === '정상' ? 'text-green-600' : 'text-red-600'}`} />
                        <span className={`text-xs font-medium ${status.status === '정상' ? 'text-green-600' : 'text-red-600'}`}>
                          {status.status}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {status.uptime}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </AuthGuard>
  );
}
